import { useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Upload as UploadIcon, FileText, CheckCircle2, X, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useSEO } from "@/lib/useSEO";

const INIT = {
  name: "", mobile: "", email: "", course: "", doc_type: "Thesis",
  total_pages: 100, bw_pages: 90, color_pages: 10, copies: 1,
  paper_size: "A4", paper_gsm: "80 GSM", print_side: "single",
  binding: "spiral", cover_color: "Navy Blue", golden_embossing: false, spine_text: "",
  lamination: false, delivery: "local", urgent: false, address: "", pincode: "", notes: "",
};

export default function UploadDocument() {
  useSEO({
    title: "Upload PDF for Thesis Printing & Hard Binding — ScholarPrintHub",
    description: "Securely upload your thesis / dissertation / research paper PDF for printing and hard binding. Golden embossing, spine printing, doorstep delivery across India.",
    keywords: "upload thesis for printing, thesis pdf print online, dissertation print upload, hard binding online india, print my thesis",
  });
  const [form, setForm] = useState(INIT);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [estimate, setEstimate] = useState(null);
  const [orderNo, setOrderNo] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  const set = (k) => (v) => setForm((s) => ({ ...s, [k]: v }));

  const onDropzoneClick = (e) => {
    if (!user) {
      e.preventDefault();
      toast.error("Please login or register first to upload files.");
      navigate("/login", { state: { from: { pathname: "/upload" } } });
    }
  };

  const onFile = async (e) => {
    const selected = Array.from(e.target.files || []);
    if (!selected.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of selected) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(data);
      }
      setFiles((prev) => [...prev, ...uploaded]);
      toast.success(`${uploaded.length} file(s) uploaded securely`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const doEstimate = async () => {
    try {
      const { data } = await api.post("/calculator/estimate", form);
      setEstimate(data);
    } catch { /* ignore */ }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...form, file_ids: files.map((f) => f.id) };
      const { data } = await api.post("/orders/print/guest", payload);
      setOrderNo(data.order_number);
      toast.success("Order created! Your order number: " + data.order_number);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create order");
    } finally {
      setSubmitting(false);
    }
  };

  if (orderNo) {
    return (
      <div className="container-app py-24 text-center max-w-2xl mx-auto" data-testid="upload-success">
        <CheckCircle2 className="w-14 h-14 mx-auto text-gold" />
        <h1 className="font-display text-4xl mt-6 text-navy">Order received</h1>
        <p className="text-slate-600 mt-3">Your printing order has been created. We will contact you shortly to confirm the proof and payment.</p>
        <div className="mt-6 inline-flex items-center gap-2 border border-slate-200 rounded-sm px-4 py-3 font-mono">
          Order number: <span className="text-navy font-semibold" data-testid="upload-order-number">{orderNo}</span>
        </div>
        <div className="mt-8 flex gap-3 justify-center">
          <a href="/track" className="btn-primary">Track order</a>
          <a href="/" className="btn-outline">Back home</a>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="upload-page" className="container-app py-16">
      <span className="section-eyebrow">Upload PDF · Printing order</span>
      <div className="divider-gold mt-2 mb-4" />
      <h1 className="font-display text-4xl sm:text-5xl text-navy">Upload your document for printing</h1>
      <p className="mt-3 text-slate-600 max-w-2xl">Fill in your requirements. We&apos;ll calculate the estimate live, and confirm the proof before printing.</p>

      <form onSubmit={submit} className="grid lg:grid-cols-12 gap-8 mt-10">
        <div className="lg:col-span-8 space-y-8">
          <Fieldset title="Your details">
            <Grid>
              <F label="Full name" testid="up-name"><Input required value={form.name} onChange={(e) => set("name")(e.target.value)} /></F>
              <F label="Mobile number" testid="up-mobile"><Input required value={form.mobile} onChange={(e) => set("mobile")(e.target.value)} /></F>
              <F label="Email" testid="up-email"><Input type="email" required value={form.email} onChange={(e) => set("email")(e.target.value)} /></F>
              <F label="Course / degree" testid="up-course"><Input value={form.course} onChange={(e) => set("course")(e.target.value)} /></F>
              <F label="Type of document" testid="up-doctype">
                <SelectC value={form.doc_type} onChange={set("doc_type")} options={["Thesis", "Dissertation", "Synopsis", "Research Paper", "Assignment", "Project Report", "Other"]} />
              </F>
            </Grid>
          </Fieldset>

          <Fieldset title="Upload files">
            <label htmlFor="upload-input" onClick={onDropzoneClick} className="border-2 border-dashed border-slate-300 rounded-sm p-8 flex flex-col items-center justify-center text-center cursor-pointer hover:border-navy transition-colors" data-testid="upload-dropzone">
              <UploadIcon className="w-8 h-8 text-navy" />
              <div className="mt-3 font-medium text-navy">Click to select PDF, DOC or DOCX</div>
              <div className="text-xs text-slate-500 mt-1">Max 50MB per file · files are encrypted in transit</div>
              <input id="upload-input" data-testid="upload-input" type="file" multiple onChange={onFile} accept=".pdf,.doc,.docx,.png,.jpg,.jpeg" className="hidden" />
              {uploading && <div className="mt-4 flex items-center gap-2 text-sm text-royal"><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</div>}
            </label>
            {files.length > 0 && (
              <ul className="mt-4 space-y-2">
                {files.map((f) => (
                  <li key={f.id} className="flex items-center justify-between border border-slate-200 rounded-sm px-3 py-2 text-sm">
                    <div className="flex items-center gap-2"><FileText className="w-4 h-4 text-royal" /><span>{f.original_filename}</span><span className="text-xs text-slate-500">({Math.round((f.size || 0) / 1024)} KB)</span></div>
                    <button type="button" onClick={() => setFiles(files.filter((x) => x.id !== f.id))} className="text-slate-400 hover:text-red-500"><X className="w-4 h-4" /></button>
                  </li>
                ))}
              </ul>
            )}
          </Fieldset>

          <Fieldset title="Printing options">
            <Grid>
              <F label="Copies" testid="up-copies"><Input type="number" min={1} value={form.copies} onChange={(e) => set("copies")(parseInt(e.target.value || "1", 10))} /></F>
              <F label="Total pages" testid="up-pages"><Input type="number" value={form.total_pages} onChange={(e) => set("total_pages")(parseInt(e.target.value || "0", 10))} /></F>
              <F label="B/W pages" testid="up-bw"><Input type="number" value={form.bw_pages} onChange={(e) => set("bw_pages")(parseInt(e.target.value || "0", 10))} /></F>
              <F label="Colour pages" testid="up-color"><Input type="number" value={form.color_pages} onChange={(e) => set("color_pages")(parseInt(e.target.value || "0", 10))} /></F>
              <F label="Paper size"><SelectC value={form.paper_size} onChange={set("paper_size")} options={["A4", "A5"]} /></F>
              <F label="Paper GSM"><SelectC value={form.paper_gsm} onChange={set("paper_gsm")} options={["70 GSM", "75 GSM", "80 GSM", "100 GSM"]} /></F>
              <F label="Printing side"><SelectC value={form.print_side} onChange={set("print_side")} options={[["single", "Single side"], ["double", "Double side"]]} /></F>
              <F label="Binding"><SelectC value={form.binding} onChange={set("binding")} options={[["none", "No binding"], ["spiral", "Spiral"], ["soft", "Soft"], ["hard", "Hard"], ["leather", "Leather hard"]]} /></F>
              <F label="Cover colour"><Input value={form.cover_color} onChange={(e) => set("cover_color")(e.target.value)} /></F>
            </Grid>
            <div className="grid sm:grid-cols-3 gap-6 mt-6">
              <ToggleR label="Golden embossing" testid="up-emboss" checked={form.golden_embossing} onChange={set("golden_embossing")} />
              <ToggleR label="Lamination" testid="up-lamination" checked={form.lamination} onChange={set("lamination")} />
              <ToggleR label="Urgent delivery" testid="up-urgent" checked={form.urgent} onChange={set("urgent")} />
            </div>
            <F label="Spine text (title, author, year)" className="mt-6"><Input value={form.spine_text} onChange={(e) => set("spine_text")(e.target.value)} /></F>
          </Fieldset>

          <Fieldset title="Delivery">
            <Grid>
              <F label="Delivery option"><SelectC value={form.delivery} onChange={set("delivery")} options={[["local", "Local (Lucknow)"], ["standard", "Standard India"], ["express", "Express"]]} /></F>
              <F label="Pincode" testid="up-pincode"><Input required value={form.pincode} onChange={(e) => set("pincode")(e.target.value)} /></F>
            </Grid>
            <F label="Delivery address" testid="up-address" className="mt-4"><Textarea required rows={3} value={form.address} onChange={(e) => set("address")(e.target.value)} /></F>
            <F label="Additional instructions" className="mt-4"><Textarea rows={3} value={form.notes} onChange={(e) => set("notes")(e.target.value)} /></F>
          </Fieldset>
        </div>

        <aside className="lg:col-span-4">
          <div className="lg:sticky lg:top-24 border border-slate-200 rounded-sm bg-navy text-white p-6">
            <div className="section-eyebrow text-gold">Live estimate</div>
            <div className="font-display text-4xl mt-2">{estimate ? `₹${Math.round(estimate.total).toLocaleString("en-IN")}` : "—"}</div>
            <div className="text-xs text-slate-300 mt-1">All-inclusive · GST extra where applicable</div>
            <button type="button" onClick={doEstimate} className="btn-outline border-white text-white hover:bg-white hover:text-navy mt-4 w-full" data-testid="up-estimate-btn">Recalculate</button>
            <button type="submit" disabled={submitting || uploading} className="btn-gold mt-3 w-full" data-testid="upload-submit">
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</> : "Place printing order"}
            </button>
            <p className="text-xs text-slate-400 mt-4">You will receive a call/WhatsApp for proof approval before we go to print.</p>
          </div>
        </aside>
      </form>
    </div>
  );
}

const Fieldset = ({ title, children }) => (
  <div className="border border-slate-200 rounded-sm p-6 bg-white">
    <div className="section-eyebrow mb-4">{title}</div>
    {children}
  </div>
);
const Grid = ({ children }) => <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">{children}</div>;
const F = ({ label, children, testid, className = "" }) => (
  <div className={className}>
    <Label className="text-xs text-slate-600">{label}</Label>
    <div className="mt-1" data-testid={testid}>{children}</div>
  </div>
);
const SelectC = ({ value, onChange, options }) => {
  const norm = options.map((o) => Array.isArray(o) ? o : [o, o]);
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
      <SelectContent>{norm.map(([v, l]) => (<SelectItem key={v} value={v}>{l}</SelectItem>))}</SelectContent>
    </Select>
  );
};
const ToggleR = ({ label, checked, onChange, testid }) => (
  <div className="flex items-center justify-between border border-slate-200 rounded-sm px-3 py-2">
    <div className="text-sm text-slate-700">{label}</div>
    <Switch data-testid={testid} checked={checked} onCheckedChange={onChange} />
  </div>
);
